// NOTE: `./types` is imported with an explicit `.ts` extension because this directory also has a
// legacy `types.js` (unrelated `SET_DEVICE_TOKEN_REQUEST` action type, consumed by `actions.js`).
// Vite/vitest's default module resolution order checks `.js` before `.ts`, so the extensionless
// specifier would silently resolve to `types.js` at runtime and leave every symbol below
// `undefined`, even though `tsc` (which prefers `.ts`) would report no error.
import {
  AuthCleanupState,
  AuthPreAuthAttemptState,
  AuthRenewalState,
  AuthSessionState,
  CleanupTransactionStatus,
  DashboardSessionStatus,
  PreAuthAttemptStatus,
  RenewalEpisodeStatus,
} from './types.ts';

/**
 * Root-store key for the auth session slice (T014, T094). Not `auth`: the legacy token/user reducer
 * keeps that key.
 */
export const AUTH_SLICE_NAME = 'authSession';

/** First valid `operationGeneration`/session generation value (data-model.md §3, §7). */
export const AUTH_INITIAL_GENERATION = 0;

/** Initial non-secret Pre-Authentication Attempt state (data-model.md §3). */
export const AUTH_INITIAL_PRE_AUTH_ATTEMPT_STATE: AuthPreAuthAttemptState = {
  generation: AUTH_INITIAL_GENERATION,
  status: PreAuthAttemptStatus.IDLE,
  decision: null,
};

/** Initial non-secret Dashboard Session state (data-model.md §7). */
export const AUTH_INITIAL_SESSION_STATE: AuthSessionState = {
  generation: AUTH_INITIAL_GENERATION,
  status: DashboardSessionStatus.UNKNOWN,
};

/** Initial Renewal Episode state (data-model.md §8). */
export const AUTH_INITIAL_RENEWAL_STATE: AuthRenewalState = {
  status: RenewalEpisodeStatus.IDLE,
};

/** Initial Cleanup Transaction state (data-model.md §9). */
export const AUTH_INITIAL_CLEANUP_STATE: AuthCleanupState = {
  status: CleanupTransactionStatus.IDLE,
};

/**
 * The Insightt brand mark's own gradient endpoints, shared by the sign-in handoff screen's ring,
 * glow and progress bar (#4243 FR-001).
 *
 * `sloth-ui-web`'s token set has no purple — the nearest, `lightLavender`, is a neutral grey — so
 * these two are named here rather than inlined as hex (Constitution VI "named constants for
 * repeated values"; VIII forbids ad-hoc colour only where a token already covers the need, and
 * none covers this one — research.md R7). The values are taken from the brand mark itself, so the
 * screen cannot drift from the logo it sits beside. Contributing a purple token upstream to
 * `sloth-ui-web` is the recorded follow-up.
 */
export const AUTH_BRAND_GRADIENT_START = '#00A2F5';
export const AUTH_BRAND_GRADIENT_END = '#7038E5';

/** The 135° blue-to-purple sweep used by every gradient element on the handoff screen. */
export const AUTH_BRAND_GRADIENT = `linear-gradient(135deg, ${AUTH_BRAND_GRADIENT_START}, ${AUTH_BRAND_GRADIENT_END})`;
